import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import TopNav from "@/components/TopNav";
import PhotoUpload from "@/components/PhotoUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, SignIn, TreeStructure, CheckCircle, Hourglass, Camera, ChatCircleText } from "@phosphor-icons/react";
import PlanPickerDialog from "@/components/PlanPickerDialog";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", description: "" });
  const [joinCode, setJoinCode] = useState("");
  const [unreadFeedback, setUnreadFeedback] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/groups");
      setGroups(data);
    } catch (e) {
      // 401s are already handled by the axios interceptor (redirect to login).
      // For any other failure, surface the real error rather than a generic message.
      if (e?.response?.status !== 401) {
        toast.error(formatApiError(e) || e.message || "Couldn't reach the server");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/feedback/unread-count");
      setUnreadFeedback(data.count || 0);
    } catch {
      // Silently ignore — the badge just won't appear.
    }
  };

  useEffect(() => { load(); loadUnread(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const createGroup = async () => {
    // Deprecated: creation now goes through Stripe Checkout (PlanPickerDialog).
    return null;
  };

  const joinGroup = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/groups/join", { invite_code: joinCode });
      toast.success(data.status === "verified" ? "Joined" : "Request sent — pending admin approval");
      setJoinOpen(false);
      setJoinCode("");
      load();
      navigate(`/groups/${data.group_id}`);
    } catch (e) {
      toast.error(formatApiError(e) || "Invalid invite code");
    }
  };

  const setProfilePhoto = async (path) => {
    // No PATCH /me endpoint in MVP; use direct collection update via re-register flow not great.
    // Simple approach: add a dedicated endpoint? For MVP we use a workaround: send to /auth/me? -- skip
    toast.info("Photo uploaded. Profile photo display will be available in next iteration.");
  };

  return (
    <div className="min-h-screen bg-ivory grain-bg">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="animate-fade-in">
            <p className="text-sm tracking-[0.22em] uppercase text-forest/70 mb-3">Your family circles</p>
            <h1 className="font-display text-4xl md:text-5xl text-forest">Hello, {user?.name?.split(" ")[0]}.</h1>
            <p className="text-sageGray mt-3 max-w-lg">Open a circle to see the connection map, members, and announcements — or start a new one.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {groups.some(g => g.role === "owner" || g.role === "admin") && (
              <Link to="/admin/feedback">
                <Button variant="ghost" className="text-forest hover:bg-cream rounded-full h-11 px-5 relative" data-testid="admin-feedback-link">
                  <ChatCircleText size={18} className="mr-2" weight="duotone" /> Feedback inbox
                  {unreadFeedback > 0 && (
                    <span
                      className="ml-2 inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-gold text-forest text-[11px] font-bold"
                      data-testid="dashboard-feedback-unread-badge"
                    >
                      {unreadFeedback}
                    </span>
                  )}
                </Button>
              </Link>
            )}
            <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-forest text-forest hover:bg-cream rounded-full h-11 px-6" data-testid="open-join-group-button">
                  <SignIn size={18} className="mr-2" /> Join with invite
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle className="font-display text-2xl text-forest">Join a family circle</DialogTitle></DialogHeader>
                <form onSubmit={joinGroup} className="space-y-4 pt-2" data-testid="join-group-form">
                  <div>
                    <Label className="text-forest">Invite code</Label>
                    <Input value={joinCode} onChange={(e)=>setJoinCode(e.target.value)} required className="mt-1.5 bg-white border-sage focus-visible:ring-gold uppercase tracking-widest" data-testid="join-code-input" />
                    <p className="text-xs text-sageGray mt-2">An admin will need to approve your request.</p>
                  </div>
                  <Button type="submit" className="w-full bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="join-group-submit-button">Request to join</Button>
                </form>
              </DialogContent>
            </Dialog>

            <Button onClick={() => setCreateOpen(true)} className="bg-forest hover:bg-forestSoft text-ivory rounded-full h-11 px-6" data-testid="open-create-group-button">
              <Plus size={18} className="mr-2" weight="bold" /> Create a circle
            </Button>
            <PlanPickerDialog open={createOpen} onOpenChange={setCreateOpen} />
          </div>
        </div>

        <div className="gold-divider mb-10" />

        {loading ? (
          <p className="text-sageGray">Loading…</p>
        ) : groups.length === 0 ? (
          <div className="legacy-card p-12 text-center" data-testid="empty-groups">
            <div className="w-16 h-16 mx-auto rounded-full bg-forest/5 text-forest flex items-center justify-center mb-4">
              <TreeStructure size={32} weight="duotone" />
            </div>
            <h3 className="font-display text-2xl text-forest mb-2">No circles yet</h3>
            <p className="text-sageGray mb-6">Create a private family circle or join one with an invite code.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="groups-grid">
            {groups.map(g => (
              <button
                key={g.id}
                onClick={() => navigate(`/groups/${g.id}`)}
                className="legacy-card p-7 text-left hover:-translate-y-1 transition-transform group"
                data-testid={`group-card-${g.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="w-11 h-11 rounded-xl bg-forest text-gold flex items-center justify-center">
                    <TreeStructure size={22} weight="duotone" />
                  </span>
                  {g.status === "verified" ? (
                    <Badge className="bg-forest/10 text-forest border-0 rounded-full px-3"><CheckCircle size={14} className="mr-1" weight="fill" /> {g.role}</Badge>
                  ) : (
                    <Badge className="bg-gold/20 text-forest border-0 rounded-full px-3"><Hourglass size={14} className="mr-1" weight="fill" /> pending</Badge>
                  )}
                </div>
                <h3 className="font-display text-xl text-forest group-hover:underline decoration-gold underline-offset-4">{g.name}</h3>
                {g.description && <p className="text-sageGray text-sm mt-2 line-clamp-2">{g.description}</p>}
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
